
import { computed, defineComponent, ref } from 'vue';
import {
  ActionType, ActiveTableAction, ActiveTableColumn, useActiveTable,
} from '@/components/activeTable/useActiveTable';
import { useDocumentParsingTemplatesApi } from '@/hooks/useDocumentParsingTemplatesApi';
import { useRouter } from 'vue-router';
import ActiveTable from '@/components/activeTable/ActiveTable.vue';
import { ProductionType } from '@/hooks/useConstructor';
import { useLocalI18n } from '@/hooks/useLocalI18n';
import Btn from '@/components/btn/Btn.vue';
import { useProtectedInject } from '@/hooks/useProtectedInject';
import { CompanyIdKey } from '@/symbols';
import { commonLegacyApiRequest } from '@urrobot/core/service/commonService';
import { ApiCommand } from '@/store/modules/api';
import { useToast } from '@/hooks/useToast';
import { useDialog } from '@/hooks/useDialog';

export default defineComponent({
  name: 'AdminTemplates',
  components: {
    ActiveTable, Btn,
  },
  setup(props, { emit }) {
    const { t: translateProductionType } = useLocalI18n('permissions.production_type');
    const { t: translateTemplateMode } = useLocalI18n('admin.templates.templateModes');
    const { t: translateTemplateType } = useLocalI18n('admin.templates.templateTypes');
    const { confirmDialog } = useDialog();
    const { showToast } = useToast();
    const companyId = useProtectedInject(CompanyIdKey);
    const router = useRouter();
    const api = useDocumentParsingTemplatesApi();

    const {
      records, columns, actions, keyField, page, limit, total, fetchData,
    } = useActiveTable<any, any, 'id'>({
      keyField: 'id',
      defaultLimit: ref(20),
      columns: computed(() => ([
        {
          key: 'title',
          label: 'Имя',
        },
        {
          key: 'production_type',
          label: 'Модуль',
          format: (v: ProductionType) => translateProductionType(v),
        },
        {
          key: 'type',
          label: 'Тип',
          format: (v: ProductionType) => translateTemplateType(v),
        },
        {
          key: 'mode',
          label: 'Режим',
          format: (v: ProductionType) => translateTemplateMode(v),
        },
        {
          key: 'is_default',
          label: 'Использовать по умолчанию',
          format: (v: boolean) => (v ? 'Да' : 'Нет'),
        },
      ]) as ActiveTableColumn<any>[]),
      fetch: async ({ params }) => {
        const { status, response } = await api.fetchTemplates({
          ...params,
          filters: {
            ...params.filters,
            company_id: companyId.value,
          },
        });
        return response;
      },
      actions: computed(() => ([
        {
          key: 'edit',
          icon: 'pencil',
          async handler({ selectedItems: [id] }) {
            await router.push({
              name: 'exchange-template',
              params: {
                id,
              },
            });
          },
          types: [ActionType.record],
        },
        {
          key: 'remove',
          icon: 'trash',
          async handler({ selectedItems: [id] }) {
            const result = await confirmDialog({
              title: 'Вы уверены, что хотите удалить шаблон?',
              message: 'Подтвердите удаление',
              confirmLabel: 'Удалить',
            });
            result.closeDialog();
            if (!result.result) {
              return;
            }
            const response = await commonLegacyApiRequest({
              command: ApiCommand.removeDocumentParsingTemplate,
              params: { id },
            });
            if (!response.status) {
              showToast({
                label: 'Ошибка удаления шаблона',
              });
              return;
            }
            fetchData();
          },
          types: [ActionType.record],
        },
      ]) as ActiveTableAction<any, any>[]),
    });

    const onCreateNewTemplate = () => {
      router.push({
        name: 'exchange-template-create',
      });
    };

    return {
      columns,
      records,
      actions,
      keyField,
      page,
      limit,
      total,
      onCreateNewTemplate,
    };
  },
});
